import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Snackbar,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Delete, Search, Email } from '@mui/icons-material';
import { newsletterService } from '../services/api';

const NewslettersPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [sortBy, setSortBy] = useState('created_at-desc');
  const [pageNumber, setPageNumber] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterSource, sortBy, pageNumber]);

  const fetchRows = async () => {
    try {
      setLoading(true);
      const response = await newsletterService.getAll({
        page: pageNumber,
        limit: 10,
        search: searchQuery,
        sort: sortBy,
        source: filterSource,
      });

      let data = [];
      let paginationData = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
      };

      if (response.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
        paginationData = response.data.pagination || paginationData;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      }

      setRows(data);
      setPagination(paginationData);
    } catch (error) {
      setRows([]);
      setToast({ open: true, message: 'Error loading newsletter subscribers', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove ${row.email} from the newsletter list?`)) return;
    try {
      await newsletterService.delete(row.id);
      setToast({ open: true, message: 'Subscriber deleted', type: 'success' });
      await fetchRows();
    } catch (error) {
      setToast({
        open: true,
        message: error.response?.data?.error || 'Delete failed',
        type: 'error',
      });
    }
  };

  const pageButtons = Math.max(1, pagination.totalPages || 1);

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100%', p: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/dashboard')} sx={{ mb: 2, color: '#2c5f2d' }}>
          Back
        </Button>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
            Newsletter Subscribers
          </Typography>
          <Typography variant="body2" sx={{ color: '#667085' }}>
            Emails collected from the footer and contact page newsletter forms.
          </Typography>
        </Box>

        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search email..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPageNumber(1); }}
                InputProps={{ startAdornment: <Search sx={{ mr: 1, color: '#667085' }} /> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Source</InputLabel>
                <Select
                  value={filterSource}
                  label="Source"
                  onChange={(e) => { setFilterSource(e.target.value); setPageNumber(1); }}
                >
                  <MenuItem value="all">All sources</MenuItem>
                  <MenuItem value="footer">Footer</MenuItem>
                  <MenuItem value="contact">Contact page</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => { setSortBy(e.target.value); setPageNumber(1); }}
                >
                  <MenuItem value="created_at-desc">Date (Newest)</MenuItem>
                  <MenuItem value="created_at-asc">Date (Oldest)</MenuItem>
                  <MenuItem value="email-asc">Email (A-Z)</MenuItem>
                  <MenuItem value="email-desc">Email (Z-A)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email sx={{ color: '#2c5f2d' }} />
                <Typography sx={{ fontWeight: 700 }}>{pagination.totalItems}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ border: '1px solid #e8ecf1', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          {loading ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f8fafb', borderBottom: '2px solid #e8ecf1' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Subscribed</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length > 0 ? rows.map((row) => (
                      <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#f8fafb' } }}>
                        <TableCell sx={{ fontWeight: 500 }}>{row.email}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.source === 'contact' ? 'Contact page' : 'Footer'}
                            color={row.source === 'contact' ? 'info' : 'success'}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#667085' }}>
                          {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Button size="small" startIcon={<Delete />} onClick={() => handleDelete(row)} sx={{ color: '#c00' }}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#667085' }}>
                          No newsletter subscribers yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTop: '1px solid #e8ecf1' }}>
                <Typography variant="body2" sx={{ color: '#667085' }}>
                  Showing {rows.length > 0 ? (pageNumber - 1) * pagination.itemsPerPage + 1 : 0} to {Math.min(pageNumber * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button variant="outlined" size="small" disabled={pageNumber === 1} onClick={() => setPageNumber(pageNumber - 1)}>Previous</Button>
                  {[...Array(Math.min(pageButtons, 12))].map((_, i) => (
                    <Button
                      key={i + 1}
                      variant={pageNumber === i + 1 ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setPageNumber(i + 1)}
                      sx={{ minWidth: 36, backgroundColor: pageNumber === i + 1 ? '#059669' : 'transparent', color: pageNumber === i + 1 ? 'white' : '#667085' }}
                    >
                      {i + 1}
                    </Button>
                  ))}
                  <Button variant="outlined" size="small" disabled={pageNumber >= pageButtons} onClick={() => setPageNumber(pageNumber + 1)}>Next</Button>
                </Box>
              </Box>
            </>
          )}
        </Card>

        <Snackbar open={toast.open} autoHideDuration={6000} onClose={() => setToast({ ...toast, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.type} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default NewslettersPage;
